import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { doc, onSnapshot } from "firebase/firestore";
import { db } from "../firebase";

const norm = (v) => String(v ?? "").trim();
const lower = (v) => norm(v).toLowerCase();

const pillStyle = (kind) => {
  const base = {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    padding: "6px 10px",
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 800,
    border: "1px solid #e6e6e6",
    background: "#fff",
  };

  if (kind === "paid") return { ...base, borderColor: "#b7e3c8", background: "#ecfff2" };
  if (kind === "pending") return { ...base, borderColor: "#ffe3a3", background: "#fff8e7" };
  if (kind === "failed") return { ...base, borderColor: "#ffc1c1", background: "#fff1f1" };
  if (kind === "cancelled") return { ...base, borderColor: "#d7d7d7", background: "#f7f7f7" };
  return base;
};

const statusCardStyle = (status) => {
  if (status === "paid") {
    return {
      marginTop: 16,
      padding: 16,
      borderRadius: 14,
      border: "1px solid #b7e3c8",
      background: "#ecfff2",
    };
  }

  if (status === "failed") {
    return {
      marginTop: 16,
      padding: 16,
      borderRadius: 14,
      border: "1px solid #ffc1c1",
      background: "#fff1f1",
    };
  }

  if (status === "cancelled") {
    return {
      marginTop: 16,
      padding: 16,
      borderRadius: 14,
      border: "1px solid #d7d7d7",
      background: "#f7f7f7",
    };
  }

  return {
    marginTop: 16,
    padding: 16,
    borderRadius: 14,
    border: "1px solid #ffe3a3",
    background: "#fff8e7",
  };
};

export default function PayfastReturn() {
  const navigate = useNavigate();
  const [params] = useSearchParams();

  const tradeIdFromQuery = useMemo(() => norm(params.get("tradeId")), [params]);
  const mPaymentIdFromQuery = useMemo(() => norm(params.get("m_payment_id")), [params]);
  const purpose = useMemo(() => lower(params.get("purpose")), [params]);

  const isSponsored = purpose === "sponsored_post";
  const isInspectionFee = purpose === "inspection_fee";
  const isWorkflowPayment = !isSponsored && !isInspectionFee;

  const tradeId = useMemo(() => {
    if (tradeIdFromQuery) return tradeIdFromQuery;
    return norm(sessionStorage.getItem("pf_last_tradeId") || "");
  }, [tradeIdFromQuery]);

  const mPaymentId = useMemo(() => {
    if (mPaymentIdFromQuery) return mPaymentIdFromQuery;
    return norm(sessionStorage.getItem("pf_last_mPaymentId") || "");
  }, [mPaymentIdFromQuery]);

  const [status, setStatus] = useState("checking"); // checking | pending | paid | failed | cancelled | unknown
  const [msg, setMsg] = useState("Confirming your payment…");
  const [details, setDetails] = useState({
    paymentId: "",
    payStatus: "",
    source: "",
    stage: "",
    pfPaymentId: "",
  });

  useEffect(() => {
    if (!tradeId) {
      setStatus("unknown");
      setMsg("Missing trade reference. Please return to the relevant page and check payment status.");
      return;
    }

    const ref = isSponsored
      ? doc(db, "Trades", tradeId)
      : doc(db, "TradeWorkflows", tradeId);

    const unsub = onSnapshot(
      ref,
      (snap) => {
        if (!snap.exists()) {
          setStatus("unknown");
          setMsg(
            isSponsored
              ? "Listing not found. Please return to the listing page."
              : "Trade workflow not found. Please contact support."
          );
          return;
        }

        const data = snap.data() || {};

        if (isSponsored) {
          const sp = data?.sponsoredPostPayment || {};
          const payStatus = lower(sp?.status);
          const storedPaymentId =
            norm(sp?.mPaymentId) ||
            norm(sp?.payfast?.mPaymentId);
          const pfPaymentId = norm(sp?.pfPaymentId);

          setDetails({
            paymentId: storedPaymentId,
            payStatus,
            source: "Trades.sponsoredPostPayment",
            stage: "",
            pfPaymentId,
          });

          if (!storedPaymentId && !payStatus) {
            setStatus("checking");
            setMsg("Waiting for sponsored payment confirmation record to be created…");
            return;
          }

          if (mPaymentId && storedPaymentId && mPaymentId !== storedPaymentId) {
            setStatus("unknown");
            setMsg("Sponsored payment reference mismatch. Please return to the listing and try again.");
            return;
          }

          if (payStatus === "paid" || payStatus === "settled") {
            setStatus("paid");
            setMsg("Sponsored payment successful. Your listing promotion is active or updating now.");
            return;
          }

          if (payStatus === "failed") {
            setStatus("failed");
            setMsg("Sponsored payment failed. You can retry from the listing.");
            return;
          }

          if (payStatus === "cancelled") {
            setStatus("cancelled");
            setMsg("Sponsored payment was cancelled. You can retry from the listing.");
            return;
          }

          setStatus("pending");
          setMsg("Sponsored payment is still processing. This can take a short moment…");
          return;
        }

        if (isInspectionFee) {
          const fee = data?.inspectionFee || {};
          const payStatus = lower(fee?.status);
          const storedPaymentId =
            norm(fee?.mPaymentId) ||
            norm(fee?.payfast?.mPaymentId);
          const pfPaymentId = norm(fee?.pfPaymentId);
          const stage = norm(data?.stage);

          setDetails({
            paymentId: storedPaymentId,
            payStatus,
            source: "TradeWorkflows.inspectionFee",
            stage,
            pfPaymentId,
          });

          if (!storedPaymentId && !payStatus) {
            setStatus("checking");
            setMsg("Waiting for inspection fee payment confirmation record to be created…");
            return;
          }

          if (mPaymentId && storedPaymentId && mPaymentId !== storedPaymentId) {
            setStatus("unknown");
            setMsg("Inspection fee payment reference mismatch. Please return to the trade and try again.");
            return;
          }

          if (payStatus === "paid" || payStatus === "settled") {
            setStatus("paid");
            setMsg("Inspection fee payment successful. The workflow can now continue.");
            return;
          }

          if (payStatus === "failed") {
            setStatus("failed");
            setMsg("Inspection fee payment failed. You can retry from the trade workflow.");
            return;
          }

          if (payStatus === "cancelled") {
            setStatus("cancelled");
            setMsg("Inspection fee payment was cancelled. You can retry from the trade workflow.");
            return;
          }

          setStatus("pending");
          setMsg("Inspection fee payment is still processing. This can take a short moment…");
          return;
        }

        if (isWorkflowPayment) {
          const workflowPayment = data?.payment || {};
          const payStatus = lower(workflowPayment?.status);
          const storedPaymentId = norm(workflowPayment?.mPaymentId);
          const pfPaymentId = norm(workflowPayment?.pfPaymentId);
          const stage = norm(data?.stage);

          setDetails({
            paymentId: storedPaymentId,
            payStatus,
            source: "TradeWorkflows.payment",
            stage,
            pfPaymentId,
          });

          if (!storedPaymentId && !payStatus) {
            setStatus("checking");
            setMsg("Waiting for payment confirmation record to be created…");
            return;
          }

          if (mPaymentId && storedPaymentId && mPaymentId !== storedPaymentId) {
            setStatus("unknown");
            setMsg("Payment reference mismatch. Please return to the trade and try again.");
            return;
          }

          if (payStatus === "paid" || payStatus === "settled") {
            setStatus("paid");
            setMsg("Payment has been successful. Click below to return to the trade.");
            return;
          }

          if (payStatus === "failed") {
            setStatus("failed");
            setMsg("Payment failed. You can retry from the trade workflow.");
            return;
          }

          if (payStatus === "cancelled") {
            setStatus("cancelled");
            setMsg("Payment was cancelled. You can retry from the trade workflow.");
            return;
          }

          setStatus("pending");
          setMsg("Payment is still processing. This can take a short moment…");
        }
      },
      (err) => {
        console.error("[PayfastReturn] onSnapshot error:", err);
        setStatus("unknown");
        setMsg(
          isSponsored
            ? "Unable to verify sponsored payment right now. Please refresh or open the listing."
            : isInspectionFee
            ? "Unable to verify inspection fee payment right now. Please refresh or open the trade workflow."
            : "Unable to verify payment right now. Please refresh or open the trade workflow."
        );
      }
    );

    const t = setTimeout(() => {
      setMsg(
        isSponsored
          ? "Still processing. If this does not update soon, open the listing and refresh the sponsorship status."
          : isInspectionFee
          ? "Still processing. If this does not update soon, open the trade workflow and refresh the inspection fee status."
          : "Still processing. If this does not update soon, open the trade and refresh the workflow status."
      );
    }, 30000);

    return () => {
      clearTimeout(t);
      unsub();
    };
  }, [tradeId, mPaymentId, isSponsored, isInspectionFee, isWorkflowPayment]);

  const goTrade = () => {
    if (!tradeId) {
      navigate("/", { replace: true });
      return;
    }

    if (isSponsored) {
      navigate(`/trade/${tradeId}`, { replace: true });
      return;
    }

    navigate(`/trade/${tradeId}?tab=workflow`, { replace: true });
  };

  const goSecondary = () => {
    if (!tradeId) {
      navigate("/", { replace: true });
      return;
    }

    if (isSponsored) {
      navigate(`/trade/${tradeId}`, { replace: true });
      return;
    }

    navigate(`/trade/${tradeId}?tab=workflow`, { replace: true });
  };

  const titleText = isSponsored
    ? "PayFast Sponsored Return"
    : isInspectionFee
    ? "PayFast Inspection Fee Return"
    : "PayFast Return";

  const paidHeadline = isSponsored
    ? "Sponsored payment successful"
    : isInspectionFee
    ? "Inspection fee payment successful"
    : "Payment successful";

  const paidBody = isSponsored
    ? "Your listing promotion is active or being updated now. You can return to the listing below."
    : isInspectionFee
    ? "Your inspection fee has been confirmed successfully. You can now return to the trade workflow."
    : "Your payment has been confirmed successfully. Click the button below to return to the trade.";

  const primaryButtonLabel = isSponsored
    ? "⬅ Back to listing"
    : "⬅ Back to trade";

  const secondaryButtonLabel = isSponsored
    ? "📄 Open listing"
    : "🧾 Open trade workflow";

  return (
    <div style={{ maxWidth: 860, margin: "48px auto", padding: "0 16px" }}>
      <div
        style={{
          border: "1px solid #e6e6e6",
          borderRadius: 18,
          background: "#fff",
          padding: 20,
          boxShadow: "0 10px 24px rgba(0,0,0,0.04)",
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", gap: 14, flexWrap: "wrap" }}>
          <div>
            <div style={{ fontSize: 22, fontWeight: 900, letterSpacing: -0.2 }}>{titleText}</div>
            <div style={{ marginTop: 6, opacity: 0.82, lineHeight: 1.5 }}>{msg}</div>
          </div>

          <div style={pillStyle(status)}>
            {status === "paid" && "✅ Confirmed"}
            {status === "pending" && "⏳ Pending"}
            {status === "failed" && "❌ Failed"}
            {status === "cancelled" && "↩ Cancelled"}
            {(status === "checking" || status === "unknown") && "ℹ️ Checking"}
          </div>
        </div>

        {status === "paid" ? (
          <div style={statusCardStyle(status)}>
            <div style={{ fontWeight: 900, marginBottom: 8 }}>{paidHeadline}</div>
            <div style={{ fontSize: 14, lineHeight: 1.6 }}>{paidBody}</div>

            {/* <div style={{ marginTop: 14 }}>
              <button
                onClick={goTrade}
                style={{
                  padding: "10px 14px",
                  borderRadius: 12,
                  border: "1px solid #0b7a3a22",
                  background: "#0b7a3a0f",
                  cursor: "pointer",
                  fontWeight: 900,
                }}
              >
                ➜ {isSponsored ? "Back to listing" : "Back to trade"}
              </button>
            </div> */}
          </div>
        ) : null}

        {status !== "paid" ? (
          <div style={{ marginTop: 16, padding: 14, borderRadius: 14, background: "#fafafa" }}>
            <div style={{ fontWeight: 900, marginBottom: 6 }}>What happens next?</div>
            <div style={{ fontSize: 13, opacity: 0.86, lineHeight: 1.5 }}>
              {isSponsored
                ? "NexterZone confirms sponsored payment via the PayFast ITN callback. This page is for user experience only. If ITN is delayed, your listing sponsorship will update automatically once received."
                : isInspectionFee
                ? "NexterZone confirms inspection fee payment via the PayFast ITN callback. This page is for user experience only. If ITN is delayed, your workflow will update automatically once received."
                : "NexterZone confirms payment via the PayFast ITN callback. The return page is for user experience only. If ITN is delayed, your workflow will update automatically once received."}
            </div>
          </div>
        ) : null}

        <div style={{ marginTop: 16, display: "flex", gap: 10, flexWrap: "wrap" }}>
          <button
            onClick={goTrade}
            style={{
              padding: "10px 14px",
              borderRadius: 12,
              border: "1px solid #e6e6e6",
              background: "#fff",
              cursor: "pointer",
              fontWeight: 800,
            }}
          >
            {primaryButtonLabel}
          </button>

          {(status === "failed" || status === "cancelled" || status === "pending" || status === "unknown" || status === "checking") && (
            <button
              onClick={goSecondary}
              style={{
                padding: "10px 14px",
                borderRadius: 12,
                border: "1px solid #0b7a3a22",
                background: "#0b7a3a0f",
                cursor: "pointer",
                fontWeight: 900,
              }}
            >
              {secondaryButtonLabel}
            </button>
          )}
        </div>

      {/*  <div style={{ marginTop: 16, fontSize: 12, opacity: 0.78 }}>
          <div>
            purpose: <span style={{ fontFamily: "monospace" }}>{purpose || "—"}</span>
          </div>
          <div>
            tradeId: <span style={{ fontFamily: "monospace" }}>{tradeId || "—"}</span>
          </div>
          <div>
            m_payment_id (query/session): <span style={{ fontFamily: "monospace" }}>{mPaymentId || "—"}</span>
          </div>
          <div>
            payment source: <span style={{ fontFamily: "monospace" }}>{details.source || "—"}</span>
          </div>
          <div>
            payment id (stored): <span style={{ fontFamily: "monospace" }}>{details.paymentId || "—"}</span>
          </div>
          <div>
            pf_payment_id (stored): <span style={{ fontFamily: "monospace" }}>{details.pfPaymentId || "—"}</span>
          </div>
          <div>
            payment status: <span style={{ fontFamily: "monospace" }}>{details.payStatus || "—"}</span>
          </div>
          <div>
            stage: <span style={{ fontFamily: "monospace" }}>{details.stage || "—"}</span>
          </div>
        </div> */}
      </div>
    </div>
  );
}